//all sale order

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../../services/apiService';
import * as XLSX from 'xlsx';
// ----------------- HELPERS -----------------
const numberFmt = (n) => new Intl.NumberFormat('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number.isFinite(+n) ? +n : 0);

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safeText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    if (Object.keys(v).length === 0) return '';
    return v.name || v.label || v.value || '';
  }
  return String(v);
};

const isNumericCell = (v) => Number.isFinite(Number(v));

const collectAllKeys = (arr) => {
  const set = new Set();
  (arr || []).forEach((r) => {
    if (!r || typeof r !== 'object') return;
    Object.keys(r).forEach((k) => set.add(k));
  });
  return Array.from(set);
};

const orderColumns = (keys) => {
  const allowed = ['Store Name', 'Brand', 'Supplier', 'Value'];
  return allowed.filter((k) => keys.includes(k));
};

const debounce = (fn, delay = 400) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
};

const toDateInputValue = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const isValidDateString = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

// ----------------- COMPONENT -----------------
const AllSaleOrders = () => {
  const navigate = useNavigate();

  // Report
  const [data, setData] = useState([]); // API rows
  const [columns, setColumns] = useState([]); // dynamic columns
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Branch filter
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Date filter (default last 10 days)
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: toDateInputValue(tenDaysAgo),
    toDate: toDateInputValue(now),
  });

  // Item search + lazy load
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemInput, setItemInput] = useState(''); // what user types
  const [itemOptions, setItemOptions] = useState([]); // dropdown options
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemPage, setItemPage] = useState(1);
  const [itemHasMore, setItemHasMore] = useState(true);

  const ITEM_PAGE_SIZE = 50;

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 1,
  });

  // ----------------- Fetch Branches -----------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];
      const mapped = list.map((b) => ({ value: b.id, label: b.branchName }));
      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // ----------------- Fetch Items (paged, by Name) -----------------
  const fetchItemsPage = async ({ name, pageNo, append }) => {
    try {
      setItemsLoading(true);
      const apiService = new ApiService();

      const parameters = `?ItemName=${encodeURIComponent(name || '')}&pageNo=${pageNo}&pageSize=${ITEM_PAGE_SIZE}`;
      const res = await apiService.getApiParamater('GET_ITEM_QUICK', parameters);

      const rows = res?.data?.result || [];

      const options = rows
        .map((item) => {
          const itemName = item.ItemName || item.ItemDescription || item.Name || item.itemName || item.name || '';
          return itemName ? { value: itemName, label: itemName } : null;
        })
        .filter(Boolean);

      // if got full page -> likely has more
      const hasMore = rows.length >= ITEM_PAGE_SIZE;

      setItemHasMore(hasMore);
      setItemPage(pageNo);

      setItemOptions((prev) => {
        if (!append) return options;
        const seen = new Set(prev.map((x) => x.value));
        const merged = [...prev];
        for (const opt of options) if (!seen.has(opt.value)) merged.push(opt);
        return merged;
      });
    } catch (e) {
      console.error('Item fetch failed:', e);
      setItemHasMore(false);
      if (!append) setItemOptions([]);
    } finally {
      setItemsLoading(false);
    }
  };

  // Debounced search when typing (calls API even for 1 character)
  const debouncedSearchItems = useMemo(
    () =>
      debounce((name) => {
        fetchItemsPage({ name, pageNo: 1, append: false });
      }, 350),
    [],
  );

  const onItemInputChange = (newValue, actionMeta) => {
    if (actionMeta?.action !== 'input-change') return newValue;
    setItemInput(newValue);
    debouncedSearchItems(newValue); // ✅ even 1 letter
    return newValue;
  };

  const onItemMenuOpen = () => {
    if (itemsLoading) return;
    if ((itemInput || '') === '' && itemOptions.length === 0) {
      fetchItemsPage({ name: '', pageNo: 1, append: false });
    }
  };

  const onItemMenuScrollToBottom = () => {
    if (itemsLoading || !itemHasMore) return;
    fetchItemsPage({ name: itemInput || '', pageNo: itemPage + 1, append: true });
  };

  const onItemChange = (opt, actionMeta) => {
    setSelectedItem(opt);

    if (actionMeta?.action === 'select-option') {
      setItemInput(''); // optional: clear typed text after selection
    }

    if (actionMeta?.action === 'clear') {
      setItemInput('');
      setItemOptions([]);
      setItemPage(1);
      setItemHasMore(true);
    }
  };

  // ----------------- Date handlers -----------------
  const onChangeRange = (e) => {
    const { name, value } = e.target;
    setFormData((old) => ({ ...old, [name]: value }));
  };

  const validateDates = () => {
    const { fromDate, toDate } = formData;

    if (!isValidDateString(fromDate) || !isValidDateString(toDate)) {
      setErr('Please select valid From/To dates.');
      return false;
    }

    const f = new Date(fromDate);
    const t = new Date(toDate);
    if (Number.isNaN(+f) || Number.isNaN(+t)) {
      setErr('Please select valid From/To dates.');
      return false;
    }

    if (f > t) {
      setErr('From Date cannot be after To Date.');
      return false;
    }

    return true;
  };

  // ----------------- Fetch Report -----------------
  const fetchData = async () => {
    if (!validateDates()) return;

    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('ReportType', 'AllStoreSaleReport');
      params.append('BranchId', selectedBranch?.value || 0);

      // ✅ selected itemName
      const itemName = selectedItem?.value || '';
      params.append('Measure', itemName || 0);

      // ✅ Date filter
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);
      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      setData(result);

      const keys = orderColumns(collectAllKeys(result));
      setColumns(keys);

      const totalRecords = result.length;
      const totalPages = Math.max(1, Math.ceil(totalRecords / pagination.pageSize));

      setPagination((p) => ({
        ...p,
        currentPage: 1,
        totalRecords,
        totalPages,
      }));
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setData([]);
      setColumns([]);
      setPagination((p) => ({ ...p, currentPage: 1, totalRecords: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  };

  // ----------------- Page slice -----------------
  const pagedRows = useMemo(() => {
    const { currentPage, pageSize } = pagination;
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return (data || []).slice(start, end);
  }, [data, pagination]);

  // ----------------- Totals (numeric cols only) -----------------
  const totals = useMemo(() => {
    const numericCols = columns.filter((c) => data.some((r) => isNumericCell(r?.[c])));
    const sumMap = {};
    numericCols.forEach((c) => {
      sumMap[c] = data.reduce((s, r) => s + toNum(r?.[c]), 0);
    });
    return { numericCols, sumMap };
  }, [data, columns]);

  // ----------------- CSV Export -----------------
  const exportCSV = () => {
    if (!data.length || !columns.length) return;

    const headerLine = columns.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(',');
    const body = data.map((r) => columns.map((c) => `"${String(r?.[c] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headerLine}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const safeFrom = (formData.fromDate || '').replaceAll('/', '-');
    const safeTo = (formData.toDate || '').replaceAll('/', '-');

    const a = document.createElement('a');
    a.href = url;
    a.download = `sale-summary-${safeFrom}_to_${safeTo}.csv`;
    a.click();
  };
  const exportExcel = () => {
    if (!data.length || !columns.length) return;

    const header = columns.map((c) => String(c));

    const body = data.map((r) =>
      columns.map((c) => {
        const v = r?.[c];
        return isNumericCell(v) ? toNum(v) : safeText(v);
      }),
    );

    // Totals row (numeric columns only)
    const totalsRow =
      totals?.numericCols?.length > 0
        ? columns.map((c, i) => {
            if (i === 0) return 'Total';
            if (Object.prototype.hasOwnProperty.call(totals.sumMap, c)) return toNum(totals.sumMap[c]);
            return '';
          })
        : null;

    const aoa = totalsRow ? [header, ...body, totalsRow] : [header, ...body];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Optional widths
    ws['!cols'] = columns.map((c, idx) => ({ wch: idx === 0 ? 28 : 18 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'All Sale Orders');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    const itemName = selectedItem?.label ? selectedItem.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Items';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `all-sale-orders_${branchName}_${itemName}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };
  // ----------------- Pagination -----------------
  const goToPage = (page) => {
    setPagination((p) => {
      if (page < 1 || page > p.totalPages) return p;
      return { ...p, currentPage: page };
    });
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value) || 50;
    setPagination((p) => {
      const totalPages = Math.max(1, Math.ceil(p.totalRecords / newSize));
      return { ...p, pageSize: newSize, currentPage: 1, totalPages };
    });
  };

  const getPageInfoText = () => {
    const { currentPage, pageSize, totalRecords } = pagination;
    if (!totalRecords) return 'No records';
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(totalRecords, currentPage * pageSize);
    return `Showing ${start}–${end} of ${totalRecords}`;
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters */}
      <div className="row g-3 align-items-end mb-3">
        {/* Branch */}
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={setSelectedBranch}
            options={branches}
            placeholder="All Branches"
            isClearable
          />
        </div>

        {/* Item */}
        <div className="col-md-3">
          <label className="form-label">Item</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedItem}
            onChange={onItemChange}
            options={itemOptions}
            onInputChange={onItemInputChange}
            onMenuOpen={onItemMenuOpen}
            onMenuScrollToBottom={onItemMenuScrollToBottom}
            placeholder={itemsLoading ? 'Loading items...' : 'Type item name or open list...'}
            isClearable
            isLoading={itemsLoading}
            closeMenuOnSelect
            filterOption={() => true} // API already filters
            noOptionsMessage={() => (itemsLoading ? 'Loading...' : 'No items found')}
            getOptionLabel={(o) => o?.label ?? ''}
            getOptionValue={(o) => o?.value ?? ''}
          />
        </div>

        {/* From Date */}
        <div className="col-md-2">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        {/* To Date */}
        <div className="col-md-2">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>
        <div className="col-md-2 col-2">
          <button className="addItem-btn w-100" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>
        </div>
        {/* Actions */}
        <div className="col-md d-flex gap-2 d-flex justify-content-end align-items-end">
          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!data.length || loading}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!data.length || loading}>
            Export Excel
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-2">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c} style={{ whiteSpace: 'nowrap' }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && pagedRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length || 1} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan={columns.length || 1} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading &&
                pagedRows.map((r, idx) => (
                  <tr key={idx}>
                    {columns.map((c) => {
                      const val = r?.[c];
                      const num = isNumericCell(val);
                      return (
                        <td key={c} className={num ? 'text-end' : ''}>
                          {num ? numberFmt(val) : safeText(val)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>

            {/* Totals row (numeric columns only) */}
            {!loading && data.length > 0 && totals.numericCols.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  {columns.map((c, i) => {
                    const isSum = Object.prototype.hasOwnProperty.call(totals.sumMap, c);
                    if (i === 0) return <td key={c}>Total</td>;
                    return (
                      <td key={c} className={isSum ? 'text-end' : ''}>
                        {isSum ? numberFmt(totals.sumMap[c]) : ''}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 py-3 gap-2 border-top">
          <div>
            <nav aria-label="pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.currentPage === 1 || loading ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.currentPage - 1)} disabled={pagination.currentPage === 1 || loading}>
                    ‹ Prev
                  </button>
                </li>

                <li className="page-item disabled">
                  <span className="page-link">
                    {pagination.currentPage} / {pagination.totalPages}
                  </span>
                </li>

                <li className={`page-item ${pagination.currentPage === pagination.totalPages || loading ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages || loading}
                  >
                    Next ›
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <small className="text-muted">{getPageInfoText()}</small>

            <div className="d-flex align-items-center gap-1">
              <small className="text-muted">Rows per page:</small>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                disabled={loading}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(AllSaleOrders);
